import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import {
  DIVIDER_PX,
  MIN_PANEL_PX,
  ORIENTATION,
  PANEL_ORDER,
  DEFAULT_SPLIT_RATIO,
  getStackedBudget,
  getVerticalPanelHeights,
  readDualPanePrefs,
  ratioFromPointer,
  writeDualPanePrefs,
} from './dualPanePrefs';

import css from './DualPaneLayout.module.css';
import CollapsedPanelHandle from './CollapsedPanelHandle';

const DualPaneLayout = props => {
  const { townHallPanel, marketPanel, className } = props;
  const intl = useIntl();
  const containerRef = useRef(null);
  const prefsRef = useRef(readDualPanePrefs());
  const dragStateRef = useRef({ active: false, pointerId: null });

  const [orientation, setOrientation] = useState(prefsRef.current.orientation);
  const [order, setOrder] = useState(prefsRef.current.order);
  const [splitRatio, setSplitRatio] = useState(prefsRef.current.splitRatio);
  const [isDragging, setIsDragging] = useState(false);
  const [stackedBudget, setStackedBudget] = useState(getStackedBudget());

  const isHorizontal = orientation === ORIENTATION.HORIZONTAL;

  const measureContainer = useCallback(() => {
    if (containerRef.current) {
      setStackedBudget(getStackedBudget());
    }
  }, []);

  useEffect(() => {
    measureContainer();
    window.addEventListener('resize', measureContainer);
    return () => window.removeEventListener('resize', measureContainer);
  }, [measureContainer, orientation]);

  const persistPrefs = useCallback(next => {
    prefsRef.current = next;
    writeDualPanePrefs(next);
  }, []);

  useEffect(() => {
    persistPrefs({ orientation, order, splitRatio });
  }, [orientation, order, splitRatio, persistPrefs]);

  const swapOrder = () => {
    setOrder(prev =>
      prev === PANEL_ORDER.TOWNHALL_FIRST
        ? PANEL_ORDER.MARKET_FIRST
        : PANEL_ORDER.TOWNHALL_FIRST
    );
  };

  const resetSplit = () => {
    setSplitRatio(DEFAULT_SPLIT_RATIO);
  };

  const handlePointerDown = event => {
    if (event.button !== 0) {
      return;
    }
    dragStateRef.current = { active: true, pointerId: event.pointerId };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const handlePointerMove = event => {
    if (!dragStateRef.current.active || !containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const nextRatio = ratioFromPointer({
      containerRect: rect,
      clientX: event.clientX,
      clientY: event.clientY,
      orientation,
    });
    setSplitRatio(nextRatio);
  };

  const endDrag = event => {
    if (!dragStateRef.current.active) {
      return;
    }
    dragStateRef.current = { active: false, pointerId: null };
    setIsDragging(false);
    if (event?.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const firstPanel = order === PANEL_ORDER.TOWNHALL_FIRST ? townHallPanel : marketPanel;
  const secondPanel = order === PANEL_ORDER.TOWNHALL_FIRST ? marketPanel : townHallPanel;

  const showFirst = splitRatio > 0;
  const showSecond = splitRatio < 1;
  const showDivider = showFirst && showSecond;

  const verticalHeights = !isHorizontal ? getVerticalPanelHeights(splitRatio, stackedBudget) : null;

  const firstFlex = splitRatio === 1 ? '1 1 0' : `${splitRatio} 1 0`;
  const secondFlex = splitRatio === 0 ? '1 1 0' : `${1 - splitRatio} 1 0`;

  const firstPanelStyle = isHorizontal
    ? { flex: firstFlex }
    : showFirst
    ? { flex: `0 0 ${verticalHeights.firstHeight}px`, height: `${verticalHeights.firstHeight}px` }
    : undefined;

  const secondPanelStyle = isHorizontal
    ? { flex: secondFlex }
    : showSecond
    ? { flex: `0 0 ${verticalHeights.secondHeight}px`, height: `${verticalHeights.secondHeight}px` }
    : undefined;

  const toolbarLabel = intl.formatMessage({
    id:
      orientation === ORIENTATION.HORIZONTAL
        ? 'DualPaneLayout.orientationHorizontal'
        : 'DualPaneLayout.orientationVertical',
  });

  const collapsedPanelKey =
    splitRatio === 0
      ? order === PANEL_ORDER.TOWNHALL_FIRST
        ? 'townhall'
        : 'market'
      : splitRatio === 1
      ? order === PANEL_ORDER.TOWNHALL_FIRST
        ? 'market'
        : 'townhall'
      : null;

  const collapsedEdge = splitRatio === 0 ? 'start' : splitRatio === 1 ? 'end' : null;

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.toolbar} role="toolbar" aria-label={toolbarLabel}>
        <span className={css.toolbarHint}>
          <FormattedMessage id="DualPaneLayout.toolbarHint" />
        </span>
        <div className={css.toolbarActions}>
          <button
            type="button"
            className={classNames(css.toggleButton, {
              [css.toggleButtonActive]: orientation === ORIENTATION.HORIZONTAL,
            })}
            onClick={() => setOrientation(ORIENTATION.HORIZONTAL)}
            aria-pressed={orientation === ORIENTATION.HORIZONTAL}
          >
            <FormattedMessage id="DualPaneLayout.layoutSideBySide" />
          </button>
          <button
            type="button"
            className={classNames(css.toggleButton, {
              [css.toggleButtonActive]: orientation === ORIENTATION.VERTICAL,
            })}
            onClick={() => setOrientation(ORIENTATION.VERTICAL)}
            aria-pressed={orientation === ORIENTATION.VERTICAL}
          >
            <FormattedMessage id="DualPaneLayout.layoutStacked" />
          </button>
          <button type="button" className={css.toggleButton} onClick={swapOrder}>
            <FormattedMessage id="DualPaneLayout.swapPanels" />
          </button>
          <button type="button" className={css.toggleButton} onClick={resetSplit}>
            <FormattedMessage id="DualPaneLayout.resetSplit" />
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className={classNames(css.splitContainer, {
          [css.splitHorizontal]: isHorizontal,
          [css.splitVertical]: !isHorizontal,
          [css.splitDragging]: isDragging,
        })}
        style={{
          '--min-panel-px': `${MIN_PANEL_PX}px`,
          ...(isHorizontal ? {} : { minHeight: `${stackedBudget}px` }),
        }}
      >
        {showFirst ? (
          <div
            className={classNames(css.panel, { [css.panelVertical]: !isHorizontal })}
            style={firstPanelStyle}
          >
            {firstPanel}
          </div>
        ) : null}

        {showDivider ? (
          <div
            className={classNames(css.divider, {
              [css.dividerHorizontal]: isHorizontal,
              [css.dividerVertical]: !isHorizontal,
            })}
            role="separator"
            aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(splitRatio * 100)}
            aria-label={intl.formatMessage({ id: 'DualPaneLayout.resizeHandle' })}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onDoubleClick={resetSplit}
          />
        ) : null}

        {showSecond ? (
          <div
            className={classNames(css.panel, { [css.panelVertical]: !isHorizontal })}
            style={secondPanelStyle}
          >
            {secondPanel}
          </div>
        ) : null}

        {!showDivider && collapsedPanelKey && collapsedEdge ? (
          <CollapsedPanelHandle
            panelKey={collapsedPanelKey}
            edge={collapsedEdge}
            isHorizontal={isHorizontal}
            onRestore={resetSplit}
          />
        ) : null}
      </div>
    </div>
  );
};

export default DualPaneLayout;
